// Keeps the assessmentId across reloads so a prospect resumes where they left off.
// Two sources: an email follow-up link (?a=<id>, wins) and localStorage (same
// browser). The id is opaque — no PII — so it is safe to carry in the URL.

const STORAGE_KEY = 'tbo_assessment_id';
const DRAFT_KEY = 'tbo_intake_draft';
const QUERY_PARAM = 'a';
/** Assessment ids are UUIDs (crypto.randomUUID) — used to recognise a loose id. */
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

function currentUrl(): URL | null {
  try {
    return new URL(window.location.href);
  } catch {
    return null;
  }
}

/** Params carried in the hash (`#a=…&mode=…`), for hosts/embeds that use the hash. */
export function hashParams(url: URL): URLSearchParams {
  return new URLSearchParams(url.hash.replace(/^#\/?/, ''));
}

/** Pull the assessment id from the widget's own URL, tolerant of the link shape:
 *  the canonical `?a=<id>`, a hash `#a=<id>`, or a bare id sitting loose in the
 *  query/hash (e.g. a mistyped `?=<id>` or `#<id>`). Ids are opaque UUIDs — no PII. */
export function idFromUrl(url: URL): string | null {
  const fromQuery = url.searchParams.get(QUERY_PARAM);
  if (fromQuery) return fromQuery;
  const fromHash = hashParams(url).get(QUERY_PARAM);
  if (fromHash) return fromHash;
  return url.search.match(UUID_RE)?.[0] ?? url.hash.match(UUID_RE)?.[0] ?? null;
}

/** The id to resume from: URL (email/share link) first, then localStorage. */
export function readSavedId(): string | null {
  const url = currentUrl();
  const fromUrl = url ? idFromUrl(url) : null;
  if (fromUrl) return fromUrl;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function saveId(id: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* private mode / storage disabled — resume via URL only */
  }
  const url = currentUrl();
  if (url) {
    url.searchParams.set(QUERY_PARAM, id);
    url.searchParams.delete(''); // drop a stray `?=<id>` left by an awkward link
    window.history.replaceState(null, '', url.toString());
  }
}

// ── Intake draft ──────────────────────────────────────────────────────────────
// The intake stage has no server id yet, so its in-progress values live only in
// localStorage (same browser) to survive an accidental refresh.

// Namespaced by mode so the evaluate and discover drafts (different shapes) don't collide.
function draftKey(ns?: string): string {
  return ns ? `${DRAFT_KEY}_${ns}` : DRAFT_KEY;
}

export function readDraft<T>(ns?: string): T | null {
  try {
    const raw = window.localStorage.getItem(draftKey(ns));
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export function saveDraft(value: unknown, ns?: string): void {
  try {
    window.localStorage.setItem(draftKey(ns), JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

export function clearDraft(ns?: string): void {
  try {
    window.localStorage.removeItem(draftKey(ns));
  } catch {
    /* ignore */
  }
}

export function clearId(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  const url = currentUrl();
  if (url && url.searchParams.has(QUERY_PARAM)) {
    url.searchParams.delete(QUERY_PARAM);
    window.history.replaceState(null, '', url.toString());
  }
}
